import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export interface BIMetrics {
  active_mrr_cents: number;
  paying_agencies: number;
  trialing_agencies: number;
  churned_30d: number;
  new_subscriptions_30d: number;
  total_agencies: number;
  total_users: number;
  total_leads: number;
  hot_leads: number;
  leads_last_30d: number;
  computed_at: string;
}

export interface DerivedMetrics {
  arpaCents: number;
  churnRate: number; // 0..1
}

function fmtCurrency(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function fmtPercent(rate: number) {
  return `${(rate * 100).toFixed(2)}%`;
}

function fmtNumber(n: number) {
  return new Intl.NumberFormat("en-US").format(n);
}

function buildRows(m: BIMetrics, d: DerivedMetrics): Array<[string, string]> {
  return [
    ["Active MRR", fmtCurrency(m.active_mrr_cents)],
    ["Paying Agencies", fmtNumber(m.paying_agencies)],
    ["ARPA (avg)", fmtCurrency(d.arpaCents)],
    ["Trialing Agencies", fmtNumber(m.trialing_agencies)],
    ["New Subscriptions (30d)", fmtNumber(m.new_subscriptions_30d)],
    ["Churned (30d)", fmtNumber(m.churned_30d)],
    ["30-day Churn Rate", fmtPercent(d.churnRate)],
    ["Total Agencies", fmtNumber(m.total_agencies)],
    ["Total Users", fmtNumber(m.total_users)],
    ["Total Leads", fmtNumber(m.total_leads)],
    ["Hot Leads (≥85)", fmtNumber(m.hot_leads)],
    ["Leads (last 30d)", fmtNumber(m.leads_last_30d)],
  ];
}

function timestampSlug() {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvEscape(value: string) {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function exportMetricsCsv(m: BIMetrics, d: DerivedMetrics) {
  const rows = buildRows(m, d);
  const lines = [
    `LegacyGrowth Engine — Platform BI Snapshot`,
    `Generated: ${new Date().toISOString()}`,
    `Computed at: ${m.computed_at}`,
    "",
    "Metric,Value",
    ...rows.map(([k, v]) => `${csvEscape(k)},${csvEscape(v)}`),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, `legacygrowth-bi-${timestampSlug()}.csv`);
}

export function exportMetricsPdf(m: BIMetrics, d: DerivedMetrics) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });

  doc.setFontSize(18);
  doc.text("LegacyGrowth Engine", 40, 50);
  doc.setFontSize(13);
  doc.text("Platform Business Intelligence — Snapshot", 40, 72);

  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 92);
  doc.text(`Computed at: ${new Date(m.computed_at).toLocaleString()}`, 40, 106);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 130,
    head: [["Metric", "Value"]],
    body: buildRows(m, d),
    styles: { fontSize: 11, cellPadding: 8 },
    headStyles: { fillColor: [31, 56, 100], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: { 1: { halign: "right" } },
    margin: { left: 40, right: 40 },
  });

  doc.setFontSize(9);
  doc.setTextColor(140);
  doc.text(
    "Confidential — Super-Admin only. Do not distribute.",
    40,
    doc.internal.pageSize.getHeight() - 30,
  );

  doc.save(`legacygrowth-bi-${timestampSlug()}.pdf`);
}